import { Grid, Stack } from "@mui/material";
import { DataCard } from "components/DataCard";
import { HintBullet } from "components/HintBullet";
import { LDSToast } from "components/LDSToast";
import { LoadingOrEmptyWrapper } from "components/LoadingAndEmptyHandler";
import { OUTLINE_DRAFT_POLYGON_ID, OutlineDrawingMap } from "components/map/OutlineDrawingMap";
import React from "react";
import { useTranslation } from "react-i18next";
import { formatSquareMeters } from "utils/general";
import { hint } from "utils/textSymbols";

import { StepColumn } from "../layout/StepColumn";
import { MapDrawingToolbar } from "../shared/MapDrawingToolbar";
import { useOutlineStep } from "./hooks/useOutlineStep";

const HINT_KEYS = ["start", "place", "finish"] as const;

export const OutlineStep = () => {
  const { t } = useTranslation();
  const {
    addPoint,
    buildingCenter,
    clearOutline,
    finishDrawing,
    isDrawing,
    isEditing,
    isErrorOutline,
    isLoadingOutline,
    isReadOnly,
    outlineAreaM2,
    outlinePoints,
    setIsDrawing,
    setIsEditing,
    updatePoint,
  } = useOutlineStep();

  return (
    <>
      <Grid item xs={6}>
        <Stack className="h-full min-h-[64rem] flex-col gap-4">
          <MapDrawingToolbar
            deleteDisabled={isReadOnly}
            deleteLabel={t("AddBuilding.outlineStep.delete")}
            drawLabel={t("AddBuilding.outlineStep.drawOutline")}
            drawStartedNotFinished={isDrawing && !!outlinePoints.length}
            editLabel={t("AddBuilding.outlineStep.editPoints")}
            isDrawing={isDrawing}
            isEditing={isEditing}
            onDeleteClick={clearOutline}
            onDrawClick={setIsDrawing}
            onEditClick={setIsEditing}
            onEndDrawingClick={finishDrawing}
            primaryButtonsDisabled={isReadOnly}
            showEndDrawingButton
          />
          {isErrorOutline && (
            <LDSToast className="" message={t("AddBuilding.outlineStep.errorToast")} variant="error" />
          )}
          <div className="w-full flex-1 overflow-hidden rounded-md border shadow-elevation400 border-ds-subtle">
            <LoadingOrEmptyWrapper
              height="40rem"
              loadingText={t("AddBuilding.outlineStep.mapLoadingText") ?? ""}
              showLoading={isLoadingOutline}
            >
              <OutlineDrawingMap
                center={buildingCenter}
                completedPolygons={[{ id: OUTLINE_DRAFT_POLYGON_ID, points: outlinePoints }]}
                isDrawing={isDrawing}
                isEditing={isEditing}
                onAddPoint={addPoint}
                onFinish={finishDrawing}
                onUpdatePoint={updatePoint}
              />
            </LoadingOrEmptyWrapper>
          </div>
        </Stack>
      </Grid>
      <StepColumn
        subtitle={t("AddBuilding.outlineStep.subtitle") as string}
        title={t("AddBuilding.outlineStep.title") as string}
      >
        <Stack className="w-full flex-col justify-between gap-7">
          <Stack className="w-full flex-col items-start gap-7 pt-4">
            <div className="w-full rounded-xl bg-lds-surface-muted px-6 py-4 border-ds-subtle">
              {HINT_KEYS.map((key) => (
                <Stack className="mt-4 gap-2 first:mt-0" key={key}>
                  <HintBullet />
                  <p className="font-ds-body-regular-xs">{t(`AddBuilding.outlineStep.hints.${key}`)}</p>
                </Stack>
              ))}
            </div>
            <DataCard
              className="w-50"
              kpi={{
                name: t("AddBuilding.outlineStep.footprintArea") as string,
                value: formatSquareMeters(outlineAreaM2),
              }}
            />
          </Stack>
          <LDSToast
            className="mt-24 w-full"
            invisibleOnDismiss
            message={`${hint} ${t("AddBuilding.outlineStep.infoToast")}`}
          />
        </Stack>
      </StepColumn>
    </>
  );
};
